import { LineChart } from "@mantine/charts";
import { Stack, Text } from "@mantine/core";
import { VFABebiePoint } from "types/tests/specifics";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatTickAtInterval, getRoundedDomain, getTicksBetween } from "./vfaChartAxes";

interface BebieCurveProps {
  data: VFABebiePoint[];
  height?: number;
}

export default function BebieCurve({ data, height = 170 }: BebieCurveProps) {
  const { t } = useTranslation();
  const yDomain = useMemo(
    () =>
      getRoundedDomain(
        data.map(({ defect }) => defect),
        5,
        [-5, 5],
      ),
    [data],
  );
  const yTicks = useMemo(() => getTicksBetween(yDomain[0], yDomain[1], 5), [yDomain]);
  const pointTicks = useMemo(() => getTicksBetween(0, data.length, 1), [data.length]);
  const majorGridTicks = useMemo(() => getTicksBetween(0, data.length, 5), [data.length]);

  if (data.length === 0) return null;

  return (
    <Stack gap={0} w="100%">
      <Text fw={700} c="black" ta="center">
        {t("bebieCurve", "Bebie Curve")}
      </Text>
      <LineChart
        h={height}
        data={data}
        dataKey="rank"
        series={[{ name: "defect", color: "black" }]}
        curveType="linear"
        connectNulls={false}
        withLegend={false}
        withTooltip={false}
        withDots={false}
        textColor="black"
        gridColor="black"
        gridAxis="xy"
        tickLine="x"
        strokeDasharray="4 4"
        gridProps={{ verticalValues: majorGridTicks, strokeOpacity: 0.25 }}
        referenceLines={[{ y: 0, color: "black" }]}
        xAxisProps={{
          type: "number",
          domain: [0, data.length],
          ticks: pointTicks,
          interval: 0,
          minTickGap: 0,
          tickFormatter: (value) => formatTickAtInterval(value, 5),
          axisLine: { stroke: "black" },
        }}
        yAxisProps={{
          type: "number",
          domain: yDomain,
          ticks: yTicks,
          interval: 0,
          allowDecimals: false,
          reversed: true,
          width: 60,
          axisLine: { stroke: "black" },
          tickLine: { stroke: "black" },
          label: {
            value: t("relativeDefectDb", "Relative defect (dB)"),
            angle: -90,
            position: "insideLeft",
            offset: -10,
            style: {
              fill: "black",
              fontFamily: "inherit",
              fontSize: 12,
              textAnchor: "middle",
            },
          },
        }}
        xAxisLabel={t("rankedTestLocations", "Ranked test locations")}
        lineChartProps={{ margin: { top: 5, right: 55, bottom: 30, left: 25 } }}
        styles={{ axisLabel: { fill: "black" } }}
      />
    </Stack>
  );
}
